import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { TokenService } from './token.service';
import { Session } from './models';

/**
 * User-friendly error messages mapped from Cognito error codes.
 */
const COGNITO_ERROR_MAP: Record<string, string> = {
  UsernameExistsException: 'An account with this email already exists.',
  UserNotFoundException: 'No account found with this email address.',
  NotAuthorizedException: 'Incorrect email or password.',
  CodeMismatchException: 'Invalid verification code. Please try again.',
  ExpiredCodeException: 'Verification code has expired. Please request a new one.',
  InvalidPasswordException: 'Password does not meet the required policy.',
  LimitExceededException: 'Too many attempts. Please try again later.',
  UserNotConfirmedException: 'Account not verified. Please check your email for a verification code.',
  InvalidParameterException: 'Invalid input provided. Please check your entries.',
  TooManyRequestsException: 'Too many requests. Please wait and try again.',
  PasswordResetRequiredException: 'Password reset is required. Please reset your password.',
};

/**
 * AuthService wraps AWS Cognito SDK operations for the Tenant Portal.
 *
 * Provides methods for email/password sign-up, sign-in, Google federation,
 * passkey (WebAuthn) authentication, password reset, and sign-out.
 *
 * Requirements: 1.1, 1.3, 2.1, 3.1, 3.3, 4.2, 4.3, 9.4
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly userPool: CognitoUserPool;

  /**
   * Reactive session state derived from TokenService.
   * Emits the current Session when authenticated, or null when not.
   */
  readonly currentSession$: Observable<Session | null>;

  constructor(
    private readonly tokenService: TokenService,
    private readonly router: Router,
  ) {
    this.userPool = new CognitoUserPool({
      UserPoolId: environment.cognitoUserPoolId,
      ClientId: environment.cognitoClientId,
    });

    // Derive session observable from TokenService's accessToken$
    this.currentSession$ = this.tokenService.accessToken$.pipe(
      map((accessToken) => {
        if (!accessToken) {
          return null;
        }
        return {
          accessToken,
          idToken: '', // ID token is managed internally by TokenService
          refreshToken: '', // Refresh token is managed internally by TokenService
          expiresAt: 0,
        } as Session;
      })
    );
  }

  /**
   * Detect whether the current browser/device supports WebAuthn (passkeys).
   * Used to conditionally show/hide passkey authentication options.
   */
  isWebAuthnSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.PublicKeyCredential !== 'undefined'
    );
  }

  /**
   * Register a new user with email and password in the Cognito User Pool.
   * Triggers a verification email to the provided address.
   *
   * @param email - The user's email address (used as username)
   * @param password - The user's chosen password
   * @returns Promise resolving to the sign-up result
   */
  signUp(email: string, password: string): Promise<ISignUpResult> {
    const attributeList = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
    ];

    return new Promise((resolve, reject) => {
      this.userPool.signUp(
        email,
        password,
        attributeList,
        [],
        (err, result) => {
          if (err) {
            reject(this.mapCognitoError(err));
            return;
          }
          resolve(result!);
        }
      );
    });
  }

  /**
   * Confirm a user's email address with the verification code sent by Cognito.
   *
   * @param email - The user's email address
   * @param code - The 6-digit verification code
   */
  confirmSignUp(email: string, code: string): Promise<void> {
    const cognitoUser = this.createCognitoUser(email);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(code, true, (err, _result) => {
        if (err) {
          reject(this.mapCognitoError(err));
          return;
        }
        resolve();
      });
    });
  }

  /**
   * Resend the email verification code for an unconfirmed user.
   *
   * @param email - The user's email address
   */
  resendConfirmationCode(email: string): Promise<void> {
    const cognitoUser = this.createCognitoUser(email);

    return new Promise((resolve, reject) => {
      cognitoUser.resendConfirmationCode((err, _result) => {
        if (err) {
          reject(this.mapCognitoError(err));
          return;
        }
        resolve();
      });
    });
  }

  /**
   * Authenticate a user with email and password.
   * On success, stores tokens in TokenService.
   *
   * @param email - The user's email address
   * @param password - The user's password
   */
  signIn(email: string, password: string): Promise<Session> {
    const cognitoUser = this.createCognitoUser(email);
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const newSession: Session = {
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
            expiresAt: session.getAccessToken().getExpiration() * 1000,
          };
          this.tokenService.setTokens(newSession);
          resolve(newSession);
        },
        onFailure: (err) => {
          reject(this.mapCognitoError(err));
        },
      });
    });
  }

  /**
   * Initiate federated sign-in with an external identity provider (Google).
   * Redirects the browser to the Cognito Hosted UI OAuth endpoint.
   *
   * @param provider - The identity provider name (currently only 'Google')
   */
  federatedSignIn(provider: 'Google'): void {
    const domain = environment.cognitoDomain;
    const clientId = environment.cognitoClientId;
    const callbackUrl = encodeURIComponent(environment.callbackUrl);
    const scopes = encodeURIComponent('openid email profile');

    const authUrl =
      `https://${domain}/oauth2/authorize?` +
      `identity_provider=${provider}&` +
      `redirect_uri=${callbackUrl}&` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `scope=${scopes}`;

    window.location.href = authUrl;
  }

  /**
   * Register a passkey (WebAuthn/FIDO2 credential) for the current user.
   * Invokes the Cognito WebAuthn registration flow.
   *
   * Requires an authenticated session and WebAuthn browser support.
   */
  async registerPasskey(): Promise<void> {
    if (!this.isWebAuthnSupported()) {
      throw new Error('WebAuthn is not supported on this device or browser.');
    }

    // Get the current authenticated user from the pool
    const cognitoUser = this.userPool.getCurrentUser();
    if (!cognitoUser) {
      throw new Error('You must be signed in to register a passkey.');
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err: Error | null) => {
        if (err) {
          reject(this.mapCognitoError(err));
          return;
        }

        // Associate a FIDO2 authenticator with the user via Cognito
        // The SDK handles the WebAuthn ceremony internally
        (cognitoUser as any).associateSoftwareToken({
          associateSecretCode: (_secretCode: string) => {
            // For WebAuthn, the browser handles credential creation
            resolve();
          },
          onFailure: (associateErr: Error) => {
            reject(this.mapCognitoError(associateErr));
          },
        });
      });
    });
  }

  /**
   * Authenticate using a registered passkey (WebAuthn/FIDO2).
   * Initiates the Cognito WebAuthn authentication flow.
   */
  async signInWithPasskey(): Promise<Session> {
    if (!this.isWebAuthnSupported()) {
      throw new Error('WebAuthn is not supported on this device or browser.');
    }

    // Passkey sign-in uses the Cognito CUSTOM_AUTH flow with WebAuthn challenge
    // This redirects through the Cognito domain for FIDO2 authentication
    const domain = environment.cognitoDomain;
    const clientId = environment.cognitoClientId;
    const callbackUrl = encodeURIComponent(environment.callbackUrl);
    const scopes = encodeURIComponent('openid email profile');

    const authUrl =
      `https://${domain}/oauth2/authorize?` +
      `redirect_uri=${callbackUrl}&` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `scope=${scopes}`;

    window.location.href = authUrl;

    // The actual session will be established via the auth callback
    // Return a never-resolving promise since the page will redirect
    return new Promise(() => {});
  }

  /**
   * Initiate the forgot password flow. Sends a verification code to the user's email.
   * Shows a generic message regardless of whether the email exists (prevents enumeration).
   *
   * @param email - The user's email address
   */
  forgotPassword(email: string): Promise<void> {
    const cognitoUser = this.createCognitoUser(email);

    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          // For UserNotFoundException, resolve silently to prevent email enumeration
          if (err.name === 'UserNotFoundException') {
            resolve();
            return;
          }
          reject(this.mapCognitoError(err));
        },
        inputVerificationCode: () => {
          // Code has been sent successfully
          resolve();
        },
      });
    });
  }

  /**
   * Complete the password reset by submitting the verification code and new password.
   *
   * @param email - The user's email address
   * @param code - The verification code received via email
   * @param newPassword - The new password to set
   */
  confirmForgotPassword(
    email: string,
    code: string,
    newPassword: string
  ): Promise<void> {
    const cognitoUser = this.createCognitoUser(email);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(this.mapCognitoError(err));
        },
      });
    });
  }

  /**
   * Sign out the current user.
   * Revokes the refresh token via Cognito, then clears all tokens from memory.
   */
  signOut(): void {
    // Clear tokens immediately and redirect to login
    this.tokenService.clearTokens();

    // Attempt Cognito global sign-out in the background (best-effort)
    const cognitoUser = this.userPool.getCurrentUser();
    if (cognitoUser) {
      try {
        cognitoUser.signOut();
      } catch {
        // Ignore errors — local tokens are already cleared
      }
    }

    // Navigate to login page
    this.router.navigate(['/auth/login']);
  }

  /**
   * Exchange an OAuth authorization code for tokens.
   * Used by the AuthCallbackComponent after federated sign-in redirect.
   *
   * @param code - The authorization code from the OAuth callback URL
   */
  async exchangeCodeForTokens(code: string): Promise<Session> {
    const domain = environment.cognitoDomain;
    const clientId = environment.cognitoClientId;
    const callbackUrl = environment.callbackUrl;

    const tokenEndpoint = `https://${domain}/oauth2/token`;

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      redirect_uri: callbackUrl,
      code,
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: body.toString(),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange authorization code for tokens.');
    }

    const data = await response.json();

    const session: Session = {
      accessToken: data.access_token,
      idToken: data.id_token,
      refreshToken: data.refresh_token,
      expiresAt: Date.now() + data.expires_in * 1000,
    };

    this.tokenService.setTokens(session);
    return session;
  }

  /**
   * Map a Cognito SDK error to a user-friendly error message.
   * Returns an Error with the mapped message for display in the UI.
   */
  private mapCognitoError(err: any): Error {
    const code = err?.code || err?.name || '';
    const friendlyMessage =
      COGNITO_ERROR_MAP[code] ||
      err?.message ||
      'An unexpected error occurred. Please try again.';

    const mappedError = new Error(friendlyMessage);
    (mappedError as any).code = code;
    (mappedError as any).originalError = err;
    return mappedError;
  }

  /**
   * Create a CognitoUser instance for the given email.
   */
  private createCognitoUser(email: string): CognitoUser {
    return new CognitoUser({
      Username: email,
      Pool: this.userPool,
    });
  }
}
